// Client-side flight number lookup — no API key required.
// Detects airline name from IATA 2-letter carrier code and provides a best-effort
// Domestic / International guess for carriers with known conventions.
// When uncertain, returns flightType: null so the UI falls back to the manual dropdown.

// Carriers regularly operating to/from Sydney Airport.
// `intlOnly`: foreign carrier — all flights to/from SYD are international.
// `domesticOnly`: carrier flies domestic only within Australia.
// Otherwise a per-carrier heuristic below determines the flight type.
const AIRLINES = {
  // Australian
  QF: { name: "Qantas" },
  QFA: { name: "Qantas" },
  JQ: { name: "Jetstar" },
  JST: { name: "Jetstar" },
  VA: { name: "Virgin Australia" },
  VOZ: { name: "Virgin Australia" },
  ZL: { name: "Rex Airlines", domesticOnly: true },
  RXA: { name: "Rex Airlines", domesticOnly: true },
  QQ: { name: "Alliance Airlines", domesticOnly: true },
  UQ: { name: "Uni-Top Airlines", domesticOnly: true },
  TL: { name: "Airnorth", domesticOnly: true },

  // Foreign carriers at SYD (international)
  EK: { name: "Emirates", intlOnly: true },
  QR: { name: "Qatar Airways", intlOnly: true },
  EY: { name: "Etihad Airways", intlOnly: true },
  SQ: { name: "Singapore Airlines", intlOnly: true },
  CX: { name: "Cathay Pacific", intlOnly: true },
  BA: { name: "British Airways", intlOnly: true },
  LH: { name: "Lufthansa", intlOnly: true },
  AF: { name: "Air France", intlOnly: true },
  KL: { name: "KLM", intlOnly: true },
  AA: { name: "American Airlines", intlOnly: true },
  DL: { name: "Delta Air Lines", intlOnly: true },
  UA: { name: "United Airlines", intlOnly: true },
  AC: { name: "Air Canada", intlOnly: true },
  NZ: { name: "Air New Zealand", intlOnly: true },
  NH: { name: "All Nippon Airways", intlOnly: true },
  JL: { name: "Japan Airlines", intlOnly: true },
  KE: { name: "Korean Air", intlOnly: true },
  OZ: { name: "Asiana Airlines", intlOnly: true },
  CI: { name: "China Airlines", intlOnly: true },
  BR: { name: "EVA Air", intlOnly: true },
  TG: { name: "Thai Airways", intlOnly: true },
  MH: { name: "Malaysia Airlines", intlOnly: true },
  GA: { name: "Garuda Indonesia", intlOnly: true },
  PR: { name: "Philippine Airlines", intlOnly: true },
  VN: { name: "Vietnam Airlines", intlOnly: true },
  MU: { name: "China Eastern", intlOnly: true },
  CA: { name: "Air China", intlOnly: true },
  CZ: { name: "China Southern", intlOnly: true },
  HU: { name: "Hainan Airlines", intlOnly: true },
  FJ: { name: "Fiji Airways", intlOnly: true },
  IE: { name: "Solomon Airlines", intlOnly: true },
  ET: { name: "Ethiopian Airlines", intlOnly: true },
  SV: { name: "Saudia", intlOnly: true },
  TK: { name: "Turkish Airlines", intlOnly: true },
  LA: { name: "LATAM Airlines", intlOnly: true },
  AR: { name: "Aerolineas Argentinas", intlOnly: true },
  MS: { name: "EgyptAir", intlOnly: true },
  D7: { name: "AirAsia X", intlOnly: true },
  TR: { name: "Scoot", intlOnly: true },
  "3K": { name: "Jetstar Asia", intlOnly: true },
  BI: { name: "Royal Brunei", intlOnly: true },
};

// Per-carrier heuristics for airlines that fly BOTH domestic + international.
// Only applied when the rule is high-confidence for Sydney routes.
function guessFlightType(carrier, number) {
  if (carrier === "QF") {
    // Qantas: mainline QF1-QF399 = international, QF400-QF1999 = domestic.
    if (number >= 1 && number <= 399) return "International";
    if (number >= 400 && number <= 1999) return "Domestic";
    return null;
  }
  if (carrier === "JQ") {
    // Jetstar: JQ1-JQ199 tend to be international, JQ400+ = domestic.
    if (number >= 1 && number <= 199) return "International";
    if (number >= 400 && number <= 2999) return "Domestic";
    return null;
  }
  if (carrier === "VA") {
    // Virgin Australia: VA1-VA99 international, VA400-VA1999 domestic.
    if (number >= 1 && number <= 99) return "International";
    if (number >= 400 && number <= 1999) return "Domestic";
    return null;
  }
  return null;
}

/**
 * Parse a flight number.
 * @param {string} raw e.g. "QF 7", "qf007", "JQ402"
 * @returns {{airline: string, flightType: string|null}|null}
 */
export function detectFlight(raw) {
  if (!raw || typeof raw !== "string") return null;
  const clean = raw.trim().toUpperCase().replace(/[\s-]+/g, "");
  // Overall shape check: 2 or 3 char carrier + 1-4 digit number + optional suffix letter.
  if (!/^[A-Z0-9]{2,3}\d{1,4}[A-Z]?$/.test(clean)) return null;

  // Try 2-char carrier first (most IATA codes), then fall back to 3-char (ICAO).
  const candidates = [
    { carrier: clean.slice(0, 2), rest: clean.slice(2) },
    { carrier: clean.slice(0, 3), rest: clean.slice(3) },
  ];
  for (const { carrier, rest } of candidates) {
    const entry = AIRLINES[carrier];
    if (!entry) continue;
    const m = rest.match(/^(\d{1,4})[A-Z]?$/);
    if (!m) continue;
    const number = parseInt(m[1], 10);
    if (Number.isNaN(number)) continue;

    let flightType = null;
    if (entry.intlOnly) flightType = "International";
    else if (entry.domesticOnly) flightType = "Domestic";
    else flightType = guessFlightType(carrier, number);

    return { airline: entry.name, flightType };
  }
  return null;
}
